import React from "react";
import { connect } from "react-redux";
import DashboardActionBar from "../../DashboardActionBar";
import Modal from "../../Modal";
import Loading from "../../Loading";
import TableOverlay from "../../TableOverlay";
import { ajaxFeedbackStatusFilter, getStarsJSX, setFeedbackStatus } from "../../redux/FeedbackRedux";
import { notifyError } from "../../ToastMessages";
import FilterBar from "../../v2/FilterBar";
import FeedbackInbox from "../../v2/FeedbackInbox";
import SearchBar from "../../SearchBar";
import SmartTableGrid from "../../SmartTableGrid";

class AdminDashboard extends React.Component {

    constructor(props) {
        super(props);

        props.dispatch(ajaxFeedbackStatusFilter(props.state, "All", null, null, null, true));
        props.dispatch(setFeedbackStatus("All"));

        this.state = {
            apiStatistics: [],
            apiDataTotals: [],
            statsLoading: true,
            totalsLoading: true,
            statsError: null,
            totalsError: null,
            timeframe: this.props.state.timeframe
        };
    }

    fetchStatisticsData = (timeframe, dateFrom = null, dateTo = null) => {
        this.setState({ statsLoading: true, statsError: null });

        const params = new URLSearchParams({
            timeframe: timeframe || ""
        });

        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        fetch(`/feedback-dashboard/statistics?${params.toString()}`, {
            method: "GET",
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(__("Failed to load statistics. Please try again."));
                }
                return response.json();
            })
            .then(data => {
                if (this._isMounted) {
                    this.setState({ apiStatistics: data, statsLoading: false });
                }
            })
            .catch(err => {
                if (this._isMounted) {
                    this.setState({ statsLoading: false, statsError: err.message });
                    notifyError(err.message);
                }
            });
    };

    fetchTotalsData = (timeframe, dateFrom = null, dateTo = null) => {
        this.setState({ totalsLoading: true, totalsError: null });

        const params = new URLSearchParams({
            timeframe: timeframe || ""
        });

        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        fetch(`/feedback-dashboard/totals?${params.toString()}`, {
            method: "GET",
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(__("Failed to load totals. Please try again."));
                }
                return response.json();
            })
            .then(data => {
                if (this._isMounted) {
                    this.setState({ apiDataTotals: data, totalsLoading: false });
                }
            })
            .catch(err => {
                if (this._isMounted) {
                    this.setState({ totalsLoading: false, totalsError: err.message });
                    notifyError(err.message);
                }
            });
    };

    componentDidMount() {
        this._isMounted = true;

        if (window && window.localStorage.getItem("timeframe")) {
            this.setState({ timeframe: window.localStorage.getItem("timeframe") });
        }

        if (this.props.state.timeframe === "custom") {
            const startDate = window.localStorage.getItem("prev_date");
            const endDate = window.localStorage.getItem("next_date");
            this.fetchStatisticsData(this.props.state.timeframe, startDate, endDate);
            this.fetchTotalsData(this.props.state.timeframe, startDate, endDate);
        } else {
            this.fetchStatisticsData(this.props.state.timeframe);
            this.fetchTotalsData(this.props.state.timeframe);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleTimeframeChange = (newTimeframe) => {
        this.setState({ timeframe: newTimeframe });
        if (newTimeframe !== "custom") {
            this.fetchStatisticsData(newTimeframe);
            this.fetchTotalsData(newTimeframe);
        } else {
            const startDate = window.localStorage.getItem("prev_date");
            const endDate = window.localStorage.getItem("next_date");
            this.fetchStatisticsData(newTimeframe, startDate, endDate);
            this.fetchTotalsData(newTimeframe, startDate, endDate);
        }
    }

    retryFetch = () => {
        const { timeframe } = this.state;
        if (timeframe === "custom") {
            const startDate = window.localStorage.getItem("prev_date");
            const endDate = window.localStorage.getItem("next_date");
            this.fetchStatisticsData(timeframe, startDate, endDate);
            this.fetchTotalsData(timeframe, startDate, endDate);
        } else {
            this.fetchStatisticsData(timeframe);
            this.fetchTotalsData(timeframe);
        }
    };

    getColumns = () => {
        const feedbackStatuses = window.feedbackStatus;
        const timeframe = this.props.state.timeframe;

        const generateFeedbacksURL = (base_url, feedbackStatus, chats = null) => {
            let others = chats ? chats : this.props.other;
            let url = `${base_url}/timeframe/${timeframe}/status/${feedbackStatus}/other/${others}`;
            if (feedbackStatuses.RatingsOnly.value === feedbackStatus) {
                url += `?filterRating=1,2,3,4,5`;
            }
            return { feedbacksUrl: url };
        };

        return [
            {
                width: "20%",
                key: "businessName",
                label: __("Location"),
                sortable: true,
                defaultSort: "asc",
                searchable: true,
                sortValue: row => row.businessName || "",
                render: (value, row) => row.isTotalRow
                    ? <strong>{row.totalBusinesses} {__("Locations")}</strong>
                    : <a href={`/business/${row.businessId}/feedbacks?${this.props.state.openStatusUrl}`}>{value}</a>
            },
            ...["All", "Open", "Pending", "Resolved", "RatingsOnly"].map(statusKey => ({
                width: "8%",
                key: statusKey === "All" ? "totalFeedbacksWithoutRatingsOnly" : (statusKey.charAt(0).toLowerCase() + statusKey.slice(1)),
                label: statusKey === "All" ? "Feedbacks" : feedbackStatuses[statusKey]?.label || statusKey,
                sortable: true,
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{value}</strong>;
                    const url = generateFeedbacksURL(`/business/${row.businessId}/feedbacks`, feedbackStatuses[statusKey].value);
                    return value > 0 ? <a href={url.feedbacksUrl} target="_blank">{value}</a> : 0;
                }
            })),
            {
                width: "8%",
                key: "chatsRequested",
                label: __("Chats Requested"),
                sortable: true
            },
            {
                width: "8%",
                key: "chatsStarted",
                label: __("Chats Started"),
                sortable: true
            },
            {
                width: "8%",
                key: "medianResponseTime.formatted",
                label: __("Median Resp Time"),
                sortable: true,
                sortValue: row => row.medianResponseTime?.seconds || "",
                render: value => value || __("No data")
            },
            {
                width: "8%",
                key: "averageRating",
                label: __("Ratings"),
                sortable: true,
                sortValue: row => row.averageRating || "",
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{parseFloat(value).toFixed(2)}</strong>;
                    return value > 0 ? (
                        <div>
                            <span className="mr-1 d-block">{parseFloat(value).toFixed(2)}</span>
                            <span className="font-10px">{getStarsJSX(value, "OTF", true)}</span>
                        </div>
                    ) : __("No data");
                }
            }
        ];
    };

    render() {
        const { apiStatistics, apiDataTotals, statsLoading, totalsLoading, statsError, totalsError } = this.state;
        const isLoading = statsLoading || totalsLoading;
        const error = statsError || totalsError;

        return (
            <div className="container-fluid dashboard-container">
                <Loading location={"page"} />
                <div className="row dashboard-stats sortable-table">
                    <DashboardActionBar onTimeframeChange={this.handleTimeframeChange} hideExport={false}
                                        excludeStatistics={true} doDispatch={true} />

                    <TableOverlay loading={isLoading} error={error} onRetry={this.retryFetch}>
                        <SmartTableGrid
                            data={apiStatistics}
                            totalsRow={apiDataTotals}
                            columns={this.getColumns()}
                            enableSearch={true}
                            enablePagination={true}
                            enableItemsPerPage={true}
                        />
                    </TableOverlay>
                </div>

                <SearchBar title="All Open Feedbacks" searchType="standard" excludeStatistics={true} />
                <FilterBar excludeStatistics={true} />
                <FeedbackInbox />
                <FilterBar position="bottom" excludeStatistics={true} />

                <Modal modal={this.props.state.modal} />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return { state: state };
}

export default connect(mapStateToProps)(AdminDashboard);
